import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Folder as FolderIcon, List as ListIcon, MoreVertical, Edit2, Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Folder, List, FolderColor } from "@/types";

const colorVariants: Record<FolderColor, string> = {
  blue: "from-blue-500 to-blue-600",
  green: "from-green-500 to-green-600",
  purple: "from-purple-500 to-purple-600",
  orange: "from-orange-500 to-orange-600",
  red: "from-red-500 to-red-600",
  teal: "from-teal-500 to-teal-600",
};

interface FolderCardProps {
  folder: Folder;
  lists: List[];
  onEdit: () => void;
  onDelete: () => void;
  onMove: (direction: 'up' | 'down') => void;
  index: number;
  totalItems: number;
}

export default function FolderCard({ folder, lists, onEdit, onDelete, onMove, index, totalItems }: FolderCardProps) {
  const navigate = useNavigate();
  const colorClass = colorVariants[folder.color] || colorVariants.blue;

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      layout
    >
      <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden group cursor-pointer">
        <Link to={createPageUrl(`ViewFolder?id=${folder.id}`)}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 bg-gradient-to-br ${colorClass} rounded-xl flex items-center justify-center shadow-lg`}>
                <FolderIcon className="w-5 h-5 text-white" />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.preventDefault()}>
                  <button className="p-2 hover:bg-slate-50 rounded-lg">
                    <MoreVertical className="w-4 h-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.preventDefault(); navigate(createPageUrl(`CreateList?folder_id=${folder.id}`)); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    New List in Folder
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.preventDefault(); onEdit(); }}>
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Folder
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => { e.preventDefault(); onMove('up'); }} disabled={index === 0}>
                    <ArrowUp className="w-4 h-4 mr-2" />
                    Move Up
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.preventDefault(); onMove('down'); }} disabled={index === totalItems - 1}>
                    <ArrowDown className="w-4 h-4 mr-2" />
                    Move Down
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => { e.preventDefault(); onDelete(); }} className="text-red-600 focus:text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Folder
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <h3 className="font-semibold text-slate-900 text-base mb-2 line-clamp-1 hover:text-blue-600 transition-colors">
              {folder.name}
            </h3>
            
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
              <ListIcon className="w-3 h-3" />
              <span>{lists.length} {lists.length === 1 ? 'list' : 'lists'}</span>
            </div>

            {lists.length > 0 && (
              <div className="space-y-1">
                {lists.slice(0, 10).map((list) => (
                  <Link
                    key={list.id}
                    to={createPageUrl(`EditList?id=${list.id}`)}
                    onClick={(e) => e.stopPropagation()}
                    className="block text-xs text-slate-600 line-clamp-1 pl-2 border-l-2 border-slate-200 hover:border-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded py-1 transition-colors"
                  >
                    {list.title}
                  </Link>
                ))}
                {lists.length > 10 && (
                  <p className="text-xs text-slate-400 pl-2 pt-1">
                    +{lists.length - 10} more
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Link>
      </Card>
    </motion.div>
  );
}
